import './formats.js';
import { transformFootprint } from './building-footprint.js';
import { RC1_WORLD } from './rc1-runtime.js';
import { Type } from '@sinclair/typebox';
import { BuildingVisualLayoutSchema } from './villages.js';
import { SpawnPointSchema, spawnDelta } from './spawn-map.js';
export const StarterElementSchema = Type.Object({
    key: Type.String({ minLength: 1, maxLength: 32 }),
    type: Type.String(),
    level: Type.Integer({ minimum: 1 }),
    quarterTurns: Type.Integer({ minimum: 0, maximum: 3 }),
    visualLayout: Type.Union([BuildingVisualLayoutSchema, Type.Null()]),
    cells: Type.Array(Type.Object({
        x: Type.Integer(),
        y: Type.Integer(),
        role: Type.Union([Type.Literal('anchor'), Type.Literal('extension'), Type.Literal('body')]),
    }), { minItems: 1, maxItems: 32 }),
});
export const StarterKitSchema = Type.Object({
    version: Type.String(),
    elements: Type.Array(StarterElementSchema, { minItems: 1, maxItems: 32 }),
});
export const SpawnPoseRequestSchema = Type.Object({
    commandId: Type.String({ format: 'uuid' }),
    artifactChecksum: Type.String({ pattern: '^[a-f0-9]{64}$' }),
    kitVersion: Type.String(),
    point: SpawnPointSchema,
    quarterTurns: Type.Integer({ minimum: 0, maximum: 3 }),
    mode: Type.Union([Type.Literal('grouped'), Type.Literal('manual')]),
    playerName: Type.String({ minLength: 1, maxLength: 40 }),
    villageName: Type.String({ minLength: 1, maxLength: 60 }),
}, { additionalProperties: false });
export const StarterPoseRequestSchema = Type.Object({
    commandId: Type.String({ format: 'uuid' }),
    elementKey: Type.String({ minLength: 1, maxLength: 32 }),
    point: SpawnPointSchema,
    quarterTurns: Type.Integer({ minimum: 0, maximum: 3 }),
}, { additionalProperties: false });
export const StarterInstallationSchema = Type.Object({
    villageId: Type.String({ format: 'uuid' }),
    kit: StarterKitSchema,
    remaining: Type.Array(Type.String()),
    anchor: SpawnPointSchema,
    quarterTurns: Type.Integer(),
    referenceHeight: Type.Number(),
});
export const SpawnTerraceSchema = Type.Object({ cellX: Type.Integer(), cellY: Type.Integer(), height: Type.Number() });
/** Rotate each building's occupied cells and visual recipe together. Canonical torus coordinates. */
export function poseStarterElement(element, point, turns, relativeToHall = true, world = RC1_WORLD) {
    const anchor = relativeToHall ? { x: 0, y: 0 } : element.cells.find((c) => c.role === 'anchor');
    const cells = transformFootprint(element.cells.map((c) => ({ ...c, x: c.x - anchor.x, y: c.y - anchor.y })), point, turns, world).map(({ cellX, cellY, role }) => ({ cellX, cellY, role }));
    const quarterTurns = (element.quarterTurns + turns) % 4;
    return {
        ...element,
        quarterTurns,
        visualLayout: element.visualLayout ? { ...element.visualLayout, quarterTurns } : null,
        cells,
    };
}
export function starterElementSurfaces(element, relativeToHall = true) {
    const anchor = relativeToHall ? { x: 0, y: 0 } : element.cells.find((c) => c.role === 'anchor');
    return element.cells.map((c) => ({ x: c.x - anchor.x, y: c.y - anchor.y, halfWidth: 0.5, halfHeight: 0.5 }));
}
/** Local edits stay inside the closed footprint; the signed substrate is never replaced. */
export function terraceHeight(edits, x, y, world = RC1_WORLD) {
    return edits.find((e) => Math.abs(spawnDelta(x, e.cellX, world.widthCells)) <= 0.5 + 1e-8 &&
        Math.abs(spawnDelta(y, e.cellY, world.heightCells)) <= 0.5 + 1e-8)?.height;
}
//# sourceMappingURL=spawn-installation.js.map